import type { PixelValue } from '@lemonade-hq/cdk';
import { setElementVars } from '@vanilla-extract/dynamic';
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDelayedValue } from '../../hooks/useDelayedValue';
import { useMobileKeyboardHeight } from '../../hooks/useMobileKeyboardHeight';
import { Layout } from '../Layout/Layout';
import { Drawer } from './Drawer';
import { DRAWER_WITH_HANDLE_PADDING } from './Drawer.css';
import * as styles from './SnapDrawer.css';

export type DrawerSnapPoints = PixelValue[] | [...PixelValue[], '*'];

export interface SnapDrawerProps {
  readonly open?: boolean;
  /** leaves drawer open but moves it out of screen */
  readonly hidden?: boolean;
  readonly snapPoints?: DrawerSnapPoints;
  readonly initialSnapPointIndex?: number;
  readonly collapseOnScroll?: boolean;
  readonly children: (currentSnapIndex: number, ref?: React.RefObject<HTMLDivElement>) => React.ReactNode;
  readonly title: string;
  readonly description: string;
  readonly noExpansion?: boolean;
  readonly paddingBottom?: number;
  readonly externalClassName?: string;
}

export const SnapDrawer: React.FC<SnapDrawerProps> = ({
  children,
  snapPoints: initialSnapPoints = [],
  hidden = false,
  open = true,
  initialSnapPointIndex = 0,
  collapseOnScroll = false,
  title,
  description,
  noExpansion = false,
  paddingBottom = 0,
  externalClassName,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [snapPoints, setSnapPoints] = useState(initialSnapPoints);
  const hasCalculatedSnapPoints = snapPoints[snapPoints.length - 1] !== '*';
  const [selectedSnapIndex, setSelectedSnapPointIndex] = useState(initialSnapPointIndex);
  const [isNativeKeyboardOpen, setIsNativeKeyboardOpen] = useState(false);

  useMobileKeyboardHeight({ onChange: setIsNativeKeyboardOpen });

  useEffect(() => {
    if (!collapseOnScroll) return;

    const handleScroll = (): void => {
      setSelectedSnapPointIndex(0);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [collapseOnScroll, snapPoints]);

  useEffect(() => {
    if (initialSnapPoints.length === 0) return;

    let timeout: number;

    const getTotalHeight = (): string => {
      /* istanbul ignore if -- @preserve - can't mock null ref */
      if (contentRef.current == null) return '0px';

      const height = contentRef.current.offsetHeight;
      const totalHeight = height + DRAWER_WITH_HANDLE_PADDING;

      return `${totalHeight}px`;
    };

    const updateSnap = (): void => {
      if (noExpansion && initialSnapPoints[0] !== '*') {
        setSnapPoints([initialSnapPoints[0]]);
        return;
      }

      /* istanbul ignore if -- @preserve - can't mock null ref */
      if (contentRef.current == null) {
        timeout = window.setTimeout(updateSnap, 100);
        return;
      }

      const totalHeight = getTotalHeight();

      setSnapPoints(prev => {
        /* istanbul ignore if -- @preserve - can't mock total height */
        if (prev[prev.length - 1] === totalHeight) return prev;

        return [...prev.slice(0, -1), totalHeight] as DrawerSnapPoints;
      });
    };

    if (!hasCalculatedSnapPoints) {
      updateSnap();
    }

    let observer: MutationObserver | undefined;
    const raf = requestAnimationFrame(() => {
      if (contentRef.current == null) return;

      observer = new MutationObserver(updateSnap);

      observer.observe(contentRef.current, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true,
      });
    });

    return () => {
      observer?.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [
    open,
    initialSnapPoints,
    isNativeKeyboardOpen,
    initialSnapPointIndex,
    snapPoints,
    hasCalculatedSnapPoints,
    noExpansion,
  ]);

  const setActiveSnapPoint = useCallback(
    (activeSnap: number | string | null) => {
      if (activeSnap == null) return;

      const activeSnapIndex = snapPoints.indexOf(
        (typeof activeSnap === 'number' ? `${activeSnap}px` : activeSnap) as PixelValue,
      );

      setSelectedSnapPointIndex(activeSnapIndex);
    },
    [snapPoints],
  );

  const nextActiveSnapPoint = useCallback(() => {
    const nextIndex = selectedSnapIndex + 1;

    if (nextIndex === snapPoints.length) {
      setSelectedSnapPointIndex(0);
      return;
    }

    setSelectedSnapPointIndex(nextIndex);
  }, [selectedSnapIndex, snapPoints.length]);

  useEffect(() => {
    const additionalHeight = selectedSnapIndex === 0 ? paddingBottom : 0;
    setElementVars(document.body, {
      [styles.snapDrawerHeightVar]: `calc(${snapPoints[selectedSnapIndex]} + ${additionalHeight}px)`,
    });
  }, [open, snapPoints, selectedSnapIndex, paddingBottom]);

  const limitHeight = useDelayedValue(open, open, !open ? 0 : 100);

  if (isNativeKeyboardOpen) return;

  return (
    <Layout height={snapPoints[0]}>
      <Drawer.Root
        activeSnapPoint={snapPoints[selectedSnapIndex]}
        dismissible={false}
        modal={false}
        open={open}
        setActiveSnapPoint={setActiveSnapPoint}
        snapPoints={snapPoints}
      >
        <Drawer.Content
          className={clsx(
            {
              [styles.snapDrawerContent]: limitHeight,
              [styles.hidden]: hidden || !limitHeight,
            },
            externalClassName,
          )}
          description={description}
          isSnapped
          onHandleClick={nextActiveSnapPoint}
          showOverlay={false}
          title={title}
        >
          {children(selectedSnapIndex, contentRef)}
        </Drawer.Content>
      </Drawer.Root>
    </Layout>
  );
};
